import { useAnalyticContext } from '@application/analytic/analytic_link/mod';
import { useUser } from '@application/authentication/use_user';
import { I18nAndL10n } from '@application/i18n/i18n_and_l10n';
import { withTranslation } from '@application/i18n/with_translation';
import { validateNin } from '@application/validators';
import { CellulaFormSubmitData, Flex, Form, InputRadio, InputText, Text, useFormContext } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Title } from '@component/title.component';
import { TopBar } from '@component/top_bar.component';
import { RepresentablePatientsPicker } from '@feature/representation/representable-patients-picker.component';
import { usePatientRepresentation } from '@feature/representation/use-patient-representation';
import { useRepresentablePatients } from '@feature/representation/use-representable-patients';
import { useBooking, ViewBookingAnalyticActions } from '../../hooks/use_booking';
import { useScreenWhoIsRespondant } from './use_who_is_respondant';

type RepresentablePatient = ReturnType<typeof useRepresentablePatients>['representablePatients'][number];

export function ScreenWhoIsRespondant() {
	const screenWhoIsRespondant = useScreenWhoIsRespondant();
	const { representablePatients, currentlyRepresentedPatient, isLoading } = useRepresentablePatients();
	const analytic = useAnalyticContext();

	function handleSubmit(formData: CellulaFormSubmitData) {
		// When "Patient med ombud" is inactive the patient can only act as a representative by
		// selecting a verified representable person (which triggers a redirect). Block the manual
		// "Next" path until such a person is selected.
		if (!screenWhoIsRespondant.allowRepresentative && formData.respondentType === 'representative') {
			return {
				ok: null,
				err: I18nAndL10n.translate('View.Booking.ScreenWhoIsRespondant.ValidationError.Required.RepresentablePatient'),
			};
		}

		screenWhoIsRespondant.submit({
			respondentType: formData.respondentType === 'representative' ? 'representative' : 'me',
			asAgentFor: formData.representativeNin ?? null,
		});
	}

	function handleInvalidSubmit() {
		analytic.trackEvent(ViewBookingAnalyticActions.FormIsInvalid);
	}

	if (currentlyRepresentedPatient) {
		return null;
	}

	return (
		<>
			<TopBar.Content />

			<Flex grow={1} gap={tokens.spacing.x2}>
				<Form.Root
					options={{
						defaultValues: {
							respondentType: screenWhoIsRespondant.data.respondentType,
							representativeNin: screenWhoIsRespondant.data.representativeNin,
						},
						mode: 'onBlur',
					}}
					onSubmit={handleSubmit}
					onInvalid={handleInvalidSubmit}
				>
					<Flex grow={1} gap={tokens.spacing.x2}>
						<Title title={withTranslation('View.Booking.ScreenWhoIsRespondant.Title')} titleSize='small' />
						<ScreenWhoIsRespondantFormInputs
							representativeNinPlaceholder={screenWhoIsRespondant.data.representativeNinPlaceholder}
							allowRepresentative={screenWhoIsRespondant.allowRepresentative}
							representablePatients={currentlyRepresentedPatient ? undefined : representablePatients}
						/>
					</Flex>

					<Flex gap={tokens.spacing.x2}>
						<Form.Error />
						<Form.SubmitButton loading={isLoading} onClick={() => analytic.trackEvent('Clicked next button')}>
							{withTranslation('PW.Common.Next')}
						</Form.SubmitButton>
					</Flex>
				</Form.Root>
			</Flex>
		</>
	);
}

function ScreenWhoIsRespondantFormInputs({
	representativeNinPlaceholder,
	allowRepresentative,
	representablePatients,
}: {
	representativeNinPlaceholder: string;
	allowRepresentative: boolean;
	representablePatients?: RepresentablePatient[];
}) {
	const viewBooking = useBooking();
	const analytic = useAnalyticContext();
	const formContext = useFormContext();
	const respondentType = formContext?.control.watch('respondentType');
	const isRepresentingSomeoneElse = respondentType === 'representative';
	const { startRepresentation } = usePatientRepresentation();
	const user = useUser();

	function handleRespondentTypeChange() {
		const value = formContext?.control.getValues('respondentType');
		analytic.trackEvent(`Changed "RespondentType" to "${value}"`);
	}

	function handleRepresentativeNinChange() {
		analytic.trackEvent(`Changed "RepresentativeNin"`);
	}

	const myselfBaseLabel = I18nAndL10n.translate('View.Booking.ScreenWhoIsRespondant.Option.Me');
	const myselfLabel = user?.commonName ? `${myselfBaseLabel} (${user.commonName})` : myselfBaseLabel;

	return (
		<Flex gap={tokens.spacing.x2}>
			<InputRadio.Root
				label={withTranslation('View.Booking.ScreenWhoIsRespondant.Input.Label.WhoIsRespondant')}
				variant='outlined'
				required
				name='respondentType'
				options={{
					required: I18nAndL10n.translate('View.Booking.ScreenWhoIsRespondant.ValidationError.Required.WhoIsRespondant'),
				}}
				onChange={handleRespondentTypeChange}
			>
				<InputRadio.Item label={myselfLabel} value='me' />
				<InputRadio.Item label={withTranslation('View.Booking.ScreenWhoIsRespondant.Option.Representative')} value='representative' />
			</InputRadio.Root>
			{isRepresentingSomeoneElse ? (
				<Flex gap={tokens.spacing.x2}>
					{representablePatients && representablePatients.length > 0 ? (
						<RepresentablePatientsPicker
							representablePatients={representablePatients}
							onSelect={(person) => startRepresentation(person.Id)}
							showOrDivider={allowRepresentative}
						/>
					) : null}
					{allowRepresentative ? (
						<>
							<InputText
								label={withTranslation('Form.Input.Label.Nin')}
								name='representativeNin'
								required
								description={withTranslation('View.Booking.ScreenWhoIsRespondant.RepresentativeNin.Description')}
								placeholder={representativeNinPlaceholder}
								onBlur={handleRepresentativeNinChange}
								options={{
									required: I18nAndL10n.translate('Form.ValidationError.Required.Nin'),
									validate: {
										nin: (value) => {
											return (
												validateNin(viewBooking.getFlowData().currentCountry, value) ||
												I18nAndL10n.translate('Form.ValidationError.Format.Nin')
											);
										},
									},
								}}
							/>
							<Text variant='labelSmallRegular' color={tokens.color.content.muted}>
								{withTranslation('View.Booking.ScreenWhoIsRespondant.RepresentativeNin.Hint')}
							</Text>
						</>
					) : null}
				</Flex>
			) : null}
		</Flex>
	);
}
